import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { OutletProvider } from "@/context/OutletContext";
import { unlockAudio } from "@/lib/sound";
import LandingPage from "@/pages/LandingPage";
import OrderPage from "@/pages/OrderPage";
import OrderStatusPage from "@/pages/OrderStatusPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/pages/AdminLayout";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminMenu from "@/pages/AdminMenu";
import AdminCategories from "@/pages/AdminCategories";
import AdminTables from "@/pages/AdminTables";
import AdminSettings from "@/pages/AdminSettings";
import AdminOutlets from "@/pages/AdminOutlets";
import AdminUsers from "@/pages/AdminUsers";
import AdminVouchers from "@/pages/AdminVouchers";
import AdminReports from "@/pages/AdminReports";
import KitchenPrint from "@/pages/KitchenPrint";
import AdminOrders from "@/pages/AdminOrders";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function SuperAdminRoute({ children }) {
  const { user } = useAuth();
  if (user && user.platform_role !== "super_admin") return <Navigate to="/admin" replace />;
  return children;
}

function App() {
  // Initialize audio on first user interaction
  useEffect(() => {
    const initAudio = () => {
      unlockAudio();
      document.removeEventListener("click", initAudio);
      document.removeEventListener("keydown", initAudio);
      document.removeEventListener("touchstart", initAudio);
    };
    
    document.addEventListener("click", initAudio);
    document.addEventListener("keydown", initAudio);
    document.addEventListener("touchstart", initAudio);
    
    return () => {
      document.removeEventListener("click", initAudio);
      document.removeEventListener("keydown", initAudio);
      document.removeEventListener("touchstart", initAudio);
    };
  }, []);

  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

function AppRoutes() {
  const { pathname } = useLocation();
  const isCustomerRoute = pathname === "/" || pathname === "/order" || pathname.startsWith("/order/status/");

  return (
    <OutletProvider loadPublicOnMount={!isCustomerRoute}>
      <AuthProvider enabled={!isCustomerRoute}>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/order" element={<OrderPage />} />
          <Route path="/order/status/:orderId" element={<OrderStatusPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="menu" element={<AdminMenu />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="tables" element={<AdminTables />} />
            <Route path="vouchers" element={<AdminVouchers />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="outlets" element={<SuperAdminRoute><AdminOutlets /></SuperAdminRoute>} />
            <Route path="users" element={<SuperAdminRoute><AdminUsers /></SuperAdminRoute>} />
          </Route>
          <Route path="kitchen-print/:orderId" element={<KitchenPrint />} />
        </Routes>
      </AuthProvider>
    </OutletProvider>
  );
}

export default App;
